import angular from "angular";

function capitalize(string) {
    return !string
        ? ""
        : string.charAt(0).toUpperCase() + string.substring(1).toLowerCase();
}

const globalObject =
    typeof window !== "undefined"
        ? window
        : typeof globalThis !== "undefined"
          ? globalThis
          : {};

const intercomSettings =
    globalObject.IntercomSettings || globalObject.intercomSettings;

function callIntercom() {
    return globalObject.Intercom.apply(globalObject.Intercom, arguments);
}

let intercomExists = false;

if (angular.isFunction(globalObject.Intercom)) {
    callIntercom("reattach_activator");

    if (intercomSettings) {
        callIntercom("update", intercomSettings);
    }

    intercomExists = true;
} else {
    const queuedIntercom = function () {
        queuedIntercom.c(arguments);
    };

    queuedIntercom.q = [];
    queuedIntercom.c = function (args) {
        queuedIntercom.q.push(args);
    };

    globalObject.Intercom = queuedIntercom;
}

let instance = false;

function createScript(url, appID) {
    if (!globalObject.document || !appID) {
        return;
    }

    const scriptUrl = url + appID;

    if (globalObject.document.querySelector(`script[src="${scriptUrl}"]`)) {
        return;
    }

    const script = globalObject.document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = scriptUrl;

    const head = globalObject.document.getElementsByTagName("head")[0];

    if (head) {
        head.appendChild(script);
    }
}

const config = {
    asyncLoading: false,
    scriptUrl: "https://widget.intercom.io/widget/",
    appID: "",
    development: false,
};

function IntercomProvider() {
    const provider = this;

    angular.forEach(config, function (value, key) {
        provider[key] = function (newValue) {
            config[key] = newValue || value;

            return provider;
        };
    });

    provider.$get = [
        "$rootScope",
        "$log",
        "IntercomSettings",
        function ($rootScope, $log, IntercomSettings) {
            if (instance) {
                $log.warn(
                    "Please use consider using either $intercom or Intercom not both"
                );
            }

            instance = true;

            let options;

            function initializeOptions() {
                options = {};

                if (config.appID) {
                    options.app_id = options.app_id || config.appID;
                }

                angular.extend(options, IntercomSettings);
            }

            initializeOptions();

            if (intercomExists) {
                callIntercom("reattach_activator");
                callIntercom("update", options);
            }

            if (config.asyncLoading) {
                createScript(config.scriptUrl, config.appID);
            }

            function $intercom() {
                callIntercom.apply(null, arguments);

                return $intercom;
            }

            const methods = [
                [
                    "boot",
                    function (bootOptions) {
                        angular.extend(options, bootOptions || {});

                        if (!options.app_id && config.appID) {
                            options.app_id = config.appID;
                        }

                        if (
                            bootOptions &&
                            bootOptions.app_id &&
                            bootOptions.app_id !== options.app_id
                        ) {
                            options.app_id = bootOptions.app_id;
                        }

                        callIntercom("boot", options);

                        return $intercom;
                    },
                ],
                [
                    "update",
                    function (data) {
                        if (data) {
                            if (!data.app_id && config.appID) {
                                data.app_id = config.appID;
                            }

                            callIntercom("update", data);
                        } else {
                            callIntercom("update");
                        }

                        return $intercom;
                    },
                ],
                [
                    "trackEvent",
                    function (eventName, data) {
                        callIntercom("trackEvent", eventName, data);

                        return $intercom;
                    },
                ],
                [
                    "showMessages",
                    function () {
                        callIntercom("showMessages");

                        return $intercom;
                    },
                ],
                [
                    "showNewMessage",
                    function (data) {
                        if (data) {
                            callIntercom("showNewMessage", data);
                        } else {
                            callIntercom("showNewMessage");
                        }

                        return $intercom;
                    },
                ],
                [
                    "hideMessages",
                    function () {
                        callIntercom("hideMessages");

                        return $intercom;
                    },
                ],
                [
                    "shutdown",
                    function () {
                        callIntercom("shutdown");
                        initializeOptions();

                        return $intercom;
                    },
                ],
                [
                    "hide",
                    function () {
                        callIntercom("hide");

                        return $intercom;
                    },
                ],
                [
                    "show",
                    function () {
                        callIntercom("show");

                        return $intercom;
                    },
                ],
                [
                    "getVisitorId",
                    function () {
                        return callIntercom("getVisitorId");
                    },
                ],
                [
                    "reattachActivator",
                    function () {
                        callIntercom("reattach_activator");

                        return $intercom;
                    },
                ],
            ];

            function buildMethod(func, method) {
                $intercom[method] = func;
                $intercom[`$${method}`] = function () {
                    func.apply(globalObject.Intercom, arguments);

                    if (!$rootScope.$$phase) {
                        $rootScope.$apply();
                    }

                    return $intercom;
                };
            }

            angular.forEach(methods, function (methodDefinition) {
                buildMethod(methodDefinition[1], methodDefinition[0]);
            });

            const isEvent = {
                show: true,
                hide: true,
                activatorClick: true,
            };

            $intercom.$on = function (event, callback) {
                if (!isEvent[event]) {
                    return;
                }

                callIntercom(`on${capitalize(event)}`, function () {
                    if ($rootScope.$$phase) {
                        callback();
                    } else {
                        $rootScope.$apply(callback);
                    }
                });

                return $intercom;
            };

            $intercom.on = function (event, callback) {
                if (!isEvent[event]) {
                    return;
                }

                callIntercom(`on${capitalize(event)}`, callback);

                return $intercom;
            };

            $intercom.$$defineMethod = function (method) {
                if (!method) {
                    return;
                }

                buildMethod(function () {
                    const args = [method].concat(
                        Array.prototype.slice.call(arguments)
                    );

                    callIntercom.apply(null, args);

                    return $intercom;
                }, method);
            };

            return $intercom;
        },
    ];
}

const ngIntercomModule = angular
    .module("ngIntercom", [])
    .value("IntercomSettings", {})
    .provider("$intercom", IntercomProvider)
    .provider("Intercom", IntercomProvider);

angular.module("angular-intercom", [ngIntercomModule.name]);

export default ngIntercomModule;
